import React, { ReactNode } from 'react';
import { ProgressTracker, StepData } from './ProgressTracker';
import { OnboardingTopBar } from '../layout/OnboardingTopBar';
import { useOnboardingSession } from '../../hooks/useOnboardingSession';
import {
  ONBOARDING_SEQUENCES,
  OnboardingStep,
  OnboardingPath,
  ONBOARDING_STEP,
} from '@codemerlin/shared/constants/onboarding';
import { useNavigate, useLocation } from 'react-router-dom';
import { StepStatus } from '../../lib/onboarding-utils';
import { getOnboardingPathReselectUrl } from '../../features/onboarding/constants/navigation';

interface OnboardingLayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  onBack?: () => void;
}

const STEP_LABELS: Record<string, string> = {
  choose: 'Path',
  company: 'Company',
  scm: 'SCM',
  repos: 'Repos',
  features: 'Features',
  workitems: 'Work Items',
  policies: 'Policies',
  standards: 'Standards',
  mcp: 'MCP',
  reporting: 'Reporting',
  summary: 'Summary',
};

const OPTIONAL_STEPS: string[] = [ONBOARDING_STEP.STANDARDS, ONBOARDING_STEP.MCP];

function getStepIdFromPathname(pathname: string): OnboardingStep | undefined {
  if (pathname.endsWith('/company')) return ONBOARDING_STEP.COMPANY;
  if (pathname.endsWith('/scm')) return ONBOARDING_STEP.SCM;
  if (pathname.endsWith('/features')) return ONBOARDING_STEP.FEATURES;
  if (pathname.endsWith('/workitems')) return ONBOARDING_STEP.WORK_ITEMS;
  if (pathname.endsWith('/policies')) return ONBOARDING_STEP.POLICIES;
  if (pathname.endsWith('/standards')) return ONBOARDING_STEP.STANDARDS;
  if (pathname.endsWith('/mcp')) return ONBOARDING_STEP.MCP;
  if (pathname.endsWith('/reporting')) return ONBOARDING_STEP.REPORTING;
  if (pathname.endsWith('/repos')) return ONBOARDING_STEP.REPOS;
  if (pathname.endsWith('/summary')) return ONBOARDING_STEP.SUMMARY;
  if (pathname === '/onboarding') return ONBOARDING_STEP.CHOOSE;
  return undefined;
}

export const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({
  children,
  title,
  description,
  onBack: customOnBack,
}) => {
  const { session } = useOnboardingSession();
  const navigate = useNavigate();
  const location = useLocation();

  const path = (session?.path as OnboardingPath) ?? 'full';
  const sequence = ONBOARDING_SEQUENCES[path] ?? [];

  const urlStepId = getStepIdFromPathname(location.pathname);
  const currentStepId =
    urlStepId ?? (session?.currentStep as OnboardingStep) ?? ONBOARDING_STEP.CHOOSE;

  const currentStepIndex = sequence.findIndex((s) => s === currentStepId);
  const safeCurrentStepIndex = currentStepIndex === -1 ? 0 : currentStepIndex;

  const completedSteps = (session?.completedSteps as string[]) ?? [];

  const steps: StepData[] = sequence.map((step: OnboardingStep, index: number) => {
    let status: StepStatus = 'upcoming';
    if (index === safeCurrentStepIndex) status = 'current';
    else if (completedSteps.includes(step) || index < safeCurrentStepIndex) status = 'completed';

    return {
      id: step,
      label: STEP_LABELS[step] ?? step,
      status,
      isOptional: OPTIONAL_STEPS.includes(step),
    };
  });

  const handleNavigate = (stepId: OnboardingStep) => {
    if (stepId === ONBOARDING_STEP.CHOOSE) navigate(getOnboardingPathReselectUrl());
    else navigate(`/onboarding/${path}/${stepId}`);
  };

  const defaultHandleBack = () => {
    if (safeCurrentStepIndex > 0) {
      handleNavigate(sequence[safeCurrentStepIndex - 1] as OnboardingStep);
    } else {
      navigate(getOnboardingPathReselectUrl());
    }
  };

  const effectiveBack =
    location.pathname !== '/onboarding' ? (customOnBack ?? defaultHandleBack) : undefined;

  const progressPercentage = Math.round(((safeCurrentStepIndex + 1) / sequence.length) * 100);

  const showProgress = location.pathname !== '/onboarding';

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Slim top bar: keeps the user menu (settings, sign out, delete account)
          reachable during onboarding without the full dashboard shell */}
      <OnboardingTopBar />
      {showProgress ? (
        <ProgressTracker
          steps={steps}
          currentStepIndex={safeCurrentStepIndex + 1}
          totalSteps={sequence.length}
          progressPercentage={progressPercentage}
          currentStepLabel={STEP_LABELS[currentStepId] ?? currentStepId}
          onNavigate={handleNavigate}
          onBack={effectiveBack}
        />
      ) : null}
      {title ? (
        <div className="mx-auto max-w-3xl px-4 py-10 flex-1 w-full">
          <div className="mb-8 space-y-2 text-center">
            <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
            <p className="text-muted-foreground">{description}</p>
          </div>
          {children}
        </div>
      ) : (
        <div className="mx-auto max-w-5xl px-4 py-8 flex-1 w-full">{children}</div>
      )}
    </div>
  );
};
